/**
 * Loans: the dashboard, the book, and what came in.
 *
 * Lent out and Borrowed are two views of one screen, never one total: money
 * the business is owed and money it owes are different things, and adding
 * them would produce a number that means nothing.
 */

import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BarChart3,
  Banknote,
  CircleCheck,
  Download,
  Eye,
  HandCoins,
  History,
  Plus,
  Search,
  Settings2,
  SlidersHorizontal,
  Wallet,
} from 'lucide-react';
import {
  LOAN_CATEGORY_LABELS,
  LOAN_DIRECTION_LABELS,
  LOAN_REPAYMENT_SOURCE_LABELS,
  LOAN_STATUSES,
  LOAN_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  loanWording,
  type LoanCategory,
  type LoanDirection,
  type LoanListItem,
  type LoanSort,
  type LoanStatus,
  type PaymentMethod,
} from '@cenox/shared';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { downloadFile, qs } from '../../lib/api';
import { useLocationFilter, useLocationReady } from '../../lib/locationContext';
import { money } from '../../lib/format';
import {
  ActionMenu,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  MenuItem,
  Pagination,
  Select,
  TableSkeleton,
  cn,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { useLoanSummary, useLoans, type LoanListFilters } from './data';
import { CategoryDonut, FigureCard, LoanStatusBadge, SortHeader, categoryName, day, useHasRiders, useLoanCategories } from './parts';
import { LoanFormModal } from './LoanForm';
import { RepaymentModal } from './RepaymentModal';

type Tab = 'all' | LoanCategory;


const PAGE_SIZE = 20;

export function LoansPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const navigate = useNavigate();
  const timezone = business?.timezone ?? 'UTC';
  const locationFilter = useLocationFilter();
  const locationReady = useLocationReady();

  const [direction, setDirection] = useState<LoanDirection>('receivable');
  const [tab, setTab] = useState<Tab>('all');
  const [status, setStatus] = useState<LoanStatus | ''>('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sort, setSort] = useState<{ by: LoanSort; dir: 'asc' | 'desc' }>({ by: 'issuedOn', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [repaying, setRepaying] = useState<LoanListItem | null>(null);

  const category = tab === 'all' ? undefined : tab;
  const words = loanWording(direction);

  const filters: LoanListFilters = {
    direction,
    category,
    status: status || undefined,
    q: search.trim() || undefined,
    from: from || undefined,
    to: to || undefined,
    ...locationFilter,
    sort: sort.by,
    dir: sort.dir,
    page,
    pageSize: PAGE_SIZE,
  };
  const list = useLoans(filters, locationReady);
  const summary = useLoanSummary({ direction, category, ...locationFilter }, locationReady);
  const currency = summary.data?.currencyCode ?? business?.currencyCode ?? 'NGN';

  function reset<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function toggleSort(by: LoanSort): void {
    setSort((s) => (s.by === by ? { by, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { by, dir: by === 'counterpartyName' || by === 'reference' ? 'asc' : 'desc' }));
    setPage(1);
  }

  const riders = useHasRiders();
  const categories = useLoanCategories();
  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'all', label: 'All loans' },
    ...categories.map((c) => ({ key: c, label: LOAN_CATEGORY_LABELS[c] })),
  ];
  const activeFilterCount = [status, search.trim(), from, to].filter(Boolean).length;
  const otherCurrencies = (summary.data?.currencies ?? []).filter((c) => c !== currency);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Loans"
        description={riders ? 'Money lent to riders and staff, and money the business has borrowed' : 'Money lent to staff and others, and money the business has borrowed'}
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<BarChart3 className="size-4" />}
              onClick={() => navigate('/loans/reports')}
            >
              <span className="hidden sm:inline">Reports</span>
            </Button>
            <Button
              variant="secondary"
              icon={<Settings2 className="size-4" />}
              onClick={() => navigate('/loans/settings')}
              aria-label="Loans settings"
            >
              <span className="hidden lg:inline">Settings</span>
            </Button>
            {can('reports:export') && (
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() => void downloadFile(`/api/v1${path(`/exports${qs({ dataset: 'loans', format: 'xlsx', ...locationFilter })}`)}`, 'loans.xlsx')}
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            )}
            {can('loans:create') && (
              <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                New loan
              </Button>
            )}
          </div>
        }
      />

      {/* Which side of the book. Never summed together. */}
      <div role="radiogroup" aria-label="Loans lent or borrowed" className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
        {(['receivable', 'payable'] as const).map((d) => (
          <button
            key={d}
            type="button"
            role="radio"
            aria-checked={direction === d}
            onClick={() => {
              setDirection(d);
              setPage(1);
            }}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              direction === d ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50',
            )}
          >
            {LOAN_DIRECTION_LABELS[d]}
          </button>
        ))}
      </div>

      {summary.isError ? (
        <ErrorState message="Could not load the loan figures." onRetry={() => void summary.refetch()} />
      ) : (
        <section aria-label="Loan figures" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <FigureCard
            label={words.loaned}
            amount={summary.data?.totalLoaned.amount}
            count={summary.data?.totalLoaned.count}
            currency={currency}
            hint="principal issued"
            icon={<Banknote className="size-4" />}
          />
          <FigureCard
            label={words.repaid}
            amount={summary.data?.totalRepaid.amount}
            count={summary.data?.totalRepaid.count}
            currency={currency}
            hint="principal returned"
            tone="good"
            icon={<CircleCheck className="size-4" />}
          />
          <FigureCard
            label="Outstanding"
            amount={summary.data?.outstanding.amount}
            count={summary.data?.outstanding.count}
            currency={currency}
            hint="principal still owed"
            tone="warn"
            icon={<Wallet className="size-4" />}
          />
          <FigureCard
            label="Overdue"
            amount={summary.data?.overdue.amount}
            count={summary.data?.overdue.count}
            currency={currency}
            hint="past due and unpaid"
            tone="owed"
            icon={<AlertTriangle className="size-4" />}
          />
        </section>
      )}

      {otherCurrencies.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Figures are in {currency}. Loans in {otherCurrencies.join(', ')} are listed below in their own
          currency and are not added into these totals.
        </p>
      )}

      <Card padded={false}>
        <div className="border-b border-slate-200 px-2 sm:px-4">
          <div role="tablist" aria-label="Loan type" className="-mb-px flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => {
                  setTab(t.key);
                  setPage(1);
                }}
                className={cn(
                  'whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors',
                  tab === t.key
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3 p-3 sm:p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden />
              <input
                type="search"
                value={search}
                onChange={(e) => reset(setSearch)(e.target.value)}
                placeholder={`Search reference, ${words.counterparty.toLowerCase()}, phone or purpose`}
                aria-label="Search loans"
                className="input-base pl-9"
              />
            </div>
            <Button
              variant="secondary"
              icon={<SlidersHorizontal className="size-4" />}
              aria-expanded={showFilters}
              aria-controls="loan-filters"
              onClick={() => setShowFilters((v) => !v)}
            >
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          </div>

          {showFilters && (
            <div id="loan-filters" className="grid grid-cols-1 gap-3 rounded-lg bg-slate-50 p-3 sm:grid-cols-3">
              <Select
                label="Status"
                value={status}
                onChange={(e) => reset(setStatus)(e.target.value as LoanStatus | '')}
                placeholder="Any status"
                options={LOAN_STATUSES.map((s) => ({ value: s, label: LOAN_STATUS_LABELS[s] }))}
              />
              <Input label="Issued from" type="date" value={from} max={to || undefined} onChange={(e) => reset(setFrom)(e.target.value)} />
              <Input label="Issued to" type="date" value={to} min={from || undefined} onChange={(e) => reset(setTo)(e.target.value)} />
              {activeFilterCount > 0 && (
                <div className="sm:col-span-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setStatus('');
                      setSearch('');
                      setFrom('');
                      setTo('');
                      setPage(1);
                    }}
                  >
                    Clear filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {list.isError ? (
          <ErrorState message="Could not load loans." onRetry={() => void list.refetch()} />
        ) : list.isPending ? (
          <TableSkeleton rows={6} columns={6} />
        ) : list.data.data.length === 0 ? (
          <EmptyState
            icon={<HandCoins className="size-10" />}
            title={activeFilterCount > 0 || tab !== 'all' ? 'No loans match' : `No loans ${direction === 'receivable' ? 'lent out' : 'borrowed'} yet`}
            description={
              direction === 'receivable'
                ? riders
                  ? 'Loans to riders and staff, and their repayments, appear here.'
                  : 'Loans to staff and others, and their repayments, appear here.'
                : 'Money the business borrows, and what it pays back, appears here.'
            }
            action={
              can('loans:create') && activeFilterCount === 0 ? (
                <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                  New loan
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Cards on a phone; the table from `md` up. */}
            <ul className="divide-y divide-slate-100 md:hidden">
              {list.data.data.map((loan) => (
                <li key={loan.id} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <Link to={`/loans/${loan.id}`} className="min-w-0">
                      <p className="truncate font-medium text-slate-900">{loan.counterpartyName}</p>
                      <p className="text-xs text-slate-500">
                        {loan.reference} · {categoryName(loan.category)} · {day(loan.issuedOn)}
                      </p>
                    </Link>
                    <LoanStatusBadge status={loan.status} />
                  </div>
                  <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <dt className="text-slate-500">Principal</dt>
                      <dd className="tabular-nums text-slate-900">{money(loan.principal, loan.currencyCode)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Outstanding</dt>
                      <dd className="tabular-nums text-slate-900">{money(loan.outstanding, loan.currencyCode)}</dd>
                    </div>
                    <div>
                      <dt className="text-slate-500">Next</dt>
                      <dd className="tabular-nums text-slate-900">{loan.nextDueOn ? day(loan.nextDueOn) : '—'}</dd>
                    </div>
                  </dl>
                  <RowActions loan={loan} onRepay={() => setRepaying(loan)} compact />
                </li>
              ))}
            </ul>

            <div className="hidden overflow-x-auto md:block">
              <table aria-label="Loans" className="w-full min-w-[900px] text-sm">
                <thead className="border-y border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <SortHeader label="Reference" by="reference" sort={sort} onSort={toggleSort} />
                    <SortHeader label={words.counterparty} by="counterpartyName" sort={sort} onSort={toggleSort} />
                    <th scope="col" className="px-2 py-2.5 font-medium">Type</th>
                    <SortHeader label="Principal" by="principal" sort={sort} onSort={toggleSort} align="right" />
                    <th scope="col" className="px-2 py-2.5 text-right font-medium">Repaid</th>
                    <SortHeader label="Outstanding" by="outstanding" sort={sort} onSort={toggleSort} align="right" />
                    <th scope="col" className="px-2 py-2.5 font-medium">Status</th>
                    <SortHeader label="Start date" by="issuedOn" sort={sort} onSort={toggleSort} />
                    <SortHeader label="Next payment" by="nextDueOn" sort={sort} onSort={toggleSort} />
                    <th scope="col" className="sticky right-0 bg-slate-50 px-2 py-2.5 text-right font-medium">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.data.data.map((loan) => (
                    <tr key={loan.id} className="hover:bg-slate-50/60">
                      <td className="whitespace-nowrap px-2 py-2.5 font-medium">
                        <Link to={`/loans/${loan.id}`} className="text-brand-700 hover:underline">
                          {loan.reference}
                        </Link>
                      </td>
                      <td className="px-2 py-2.5">
                        <p className="max-w-[9rem] truncate text-slate-900">{loan.counterpartyName}</p>
                        {loan.purpose && <p className="max-w-[9rem] truncate text-xs text-slate-500">{loan.purpose}</p>}
                      </td>
                      <td className="px-2 py-2.5 text-slate-600">{categoryName(loan.category)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums">{money(loan.principal, loan.currencyCode)}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-600">{money(loan.repaid, loan.currencyCode)}</td>
                      <td className="px-2 py-2.5 text-right font-medium tabular-nums">{money(loan.outstanding, loan.currencyCode)}</td>
                      <td className="px-2 py-2.5"><LoanStatusBadge status={loan.status} /></td>
                      <td className="whitespace-nowrap px-2 py-2.5 text-slate-600">{day(loan.issuedOn)}</td>
                      <td className="px-2 py-2.5">
                        {loan.nextDueOn ? (
                          <>
                            <p className={cn('whitespace-nowrap text-slate-900', loan.status === 'overdue' && 'text-rose-700')}>{day(loan.nextDueOn)}</p>
                            <p className="text-xs tabular-nums text-slate-500">{money(loan.nextDueAmount, loan.currencyCode)}</p>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="sticky right-0 bg-white px-2 py-2.5 text-right shadow-[-6px_0_6px_-6px_rgba(15,23,42,0.15)]">
                        <RowActions loan={loan} onRepay={() => setRepaying(loan)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.meta.total} onPageChange={setPage} />
          </>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader
            title={direction === 'receivable' ? 'Recent repayments' : 'Recent payments to lenders'}
            subtitle={`The last ${summary.data?.recentRepayments.length ?? 0} recorded`}
          />
          {!summary.data || summary.data.recentRepayments.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No payments recorded yet.</p>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:-mx-5">
              <table aria-label="Recent repayments" className="w-full min-w-[520px] text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-2 font-medium sm:px-5">Date</th>
                    <th scope="col" className="px-2 py-2 font-medium">Loan</th>
                    <th scope="col" className="px-2 py-2 font-medium">{words.counterparty}</th>
                    <th scope="col" className="px-2 py-2 text-right font-medium">Amount</th>
                    <th scope="col" className="px-4 py-2 font-medium sm:px-5">Method</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {summary.data.recentRepayments.map((r) => (
                    <tr key={r.id} className={cn(r.reversed && 'text-slate-400 line-through')}>
                      <td className="whitespace-nowrap px-4 py-2 sm:px-5">{day(r.occurredOn)}</td>
                      <td className="whitespace-nowrap px-2 py-2">
                        <Link to={`/loans/${r.loanId}`} className="text-brand-700 hover:underline">{r.reference}</Link>
                      </td>
                      <td className="max-w-[10rem] truncate px-2 py-2">{r.counterpartyName}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{money(r.amount, currency)}</td>
                      <td className="px-4 py-2 text-slate-600 sm:px-5">
                        {r.source !== 'manual'
                          ? LOAN_REPAYMENT_SOURCE_LABELS[r.source]
                          : r.paymentMethod
                            ? PAYMENT_METHOD_LABELS[r.paymentMethod as PaymentMethod] ?? r.paymentMethod
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Loan types" subtitle="Outstanding principal by category" />
          {summary.data ? (
            <CategoryDonut rows={summary.data.byCategory} currency={currency} />
          ) : (
            <div className="h-40" />
          )}
        </Card>
      </div>

      <p className="text-xs text-slate-500">
        Loans are kept apart from revenue and expenses. Principal lent or borrowed never appears in
        the dashboard&apos;s profit; only interest is income or cost, shown in Loan reports.
        Dates are in {timezone}.
      </p>

      {creating && <LoanFormModal open onClose={() => setCreating(false)} defaultDirection={direction} defaultCategory={category} />}
      {repaying && <RepaymentModal loan={repaying} onClose={() => setRepaying(null)} />}
    </div>
  );
}

function RowActions({
  loan,
  onRepay,
  compact = false,
}: {
  loan: LoanListItem;
  onRepay: () => void;
  compact?: boolean;
}): ReactNode {
  const { can } = useAuth();
  const navigate = useNavigate();
  const repayable = (loan.status === 'active' || loan.status === 'overdue') && can('loans:repay');
  const words = loanWording(loan.direction);

  if (compact) {
    return (
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => navigate(`/loans/${loan.id}`)}>View</Button>
        {repayable && <Button size="sm" onClick={onRepay}>{words.record}</Button>}
      </div>
    );
  }

  return (
    <ActionMenu label={`Actions for ${loan.reference}`}>
      <MenuItem icon={<Eye className="size-4" />} onSelect={() => navigate(`/loans/${loan.id}`)}>View</MenuItem>
      {repayable && (
        <MenuItem icon={<HandCoins className="size-4" />} onSelect={onRepay}>{words.record}</MenuItem>
      )}
      {(can('loans:manage') || (loan.status === 'draft' && can('loans:create'))) && (
        <MenuItem icon={<SlidersHorizontal className="size-4" />} onSelect={() => navigate(`/loans/${loan.id}?edit=1`)}>
          Edit
        </MenuItem>
      )}
      <MenuItem icon={<History className="size-4" />} onSelect={() => navigate(`/loans/${loan.id}#history`)}>
        History
      </MenuItem>
    </ActionMenu>
  );
}
